import { ChevronRight, AccountTree } from "@mui/icons-material";
import {
  ListItemAvatar,
  ListItemText,
  ListItemSecondaryAction,
  List,
  Divider,
  ListItemButton,
} from "@mui/material";
import { sortBy } from "lodash";
import { ReactElement } from "react";
import React from "react";
import { FormattedMessage } from "react-intl";
import { StringParam, useQueryParam, withDefault } from "use-query-params";

import { InternalLink } from "~frontend/components/Link";
import PageContainer from "~frontend/components/PageContainer";
import { TableControls } from "~frontend/components/Table";
import { useBreadcrumbs } from "~frontend/providers/BreadcrumbsProvider";
import { useIntl } from "~frontend/providers/IntlProvider";
import { useCurrentUserSubaccountMemberships } from "~frontend/providers/LoggedInUserProvider/context";
import { subaccountLandingPage } from "~frontend/providers/SubaccountProvider";

function SubaccountLink({
  icon,
  title,
  href,
  note,
}: {
  icon: ReactElement;
  title: string;
  href: string;
  note?: string | ReactElement;
}): ReactElement {
  return (
    <ListItemButton component={InternalLink} to={href}>
      <ListItemAvatar>{icon}</ListItemAvatar>
      <ListItemText sx={{ paddingLeft: 1 }} primary={title} secondary={note} />
      <ListItemSecondaryAction>
        <ChevronRight />
      </ListItemSecondaryAction>
    </ListItemButton>
  );
}

export function Subaccounts(): ReactElement {
  const intl = useIntl();
  const memberships = sortBy(
    useCurrentUserSubaccountMemberships()!,
    m => m.subaccount.name
  );

  useBreadcrumbs([
    {
      label: intl.formatMessage({
        defaultMessage: "Subaccounts",
        description: "Subaccounts list page header",
      }),
      path: "/user/profile",
    },
  ]);

  const [search, setSearch] = useQueryParam(
    "search",
    withDefault(StringParam, "")
  );

  const filteredMemberships = search
    ? memberships.filter(m =>
        m.subaccount.name
          .toLocaleLowerCase()
          .includes(search.toLocaleLowerCase())
      )
    : memberships.slice(0);

  return (
    <PageContainer>
      <TableControls
        search={{
          value: search,
          onChange: value => {
            setSearch(value as string);
          },
          placeholder: "Search for a subaccount...",
        }}
      />
      <List disablePadding>
        {filteredMemberships.map(membership => (
          <React.Fragment key={membership.subaccount.id}>
            <Divider />
            <SubaccountLink
              icon={<AccountTree style={{ width: 42, height: 42 }} />}
              title={`${membership.subaccount.name} ${membership.subaccount.archivedAt ? "(ARCHIVED)" : ""}`}
              href={`/subaccounts/${membership.subaccount.slug}/${subaccountLandingPage(
                membership
              )}`}
              note={
                "createdAt" in membership && membership.createdAt ? (
                  <FormattedMessage
                    defaultMessage="Joined {date}"
                    description="Note about when a user joined a subaccount"
                    values={{
                      date: new Date(membership.createdAt).toLocaleDateString(),
                    }}
                  />
                ) : undefined
              }
            />
          </React.Fragment>
        ))}

        <Divider />
      </List>
    </PageContainer>
  );
}
